





























import { Vue, Component, Watch } from 'vue-property-decorator';
import { NetworksRepository } from '@/repository/NetworksRepository';
import { DevicesRepository } from '@/repository/DevicesRepository';
import { ReportsRepository } from '@/repository/ReportsRepository';
import { NetworkModel, DeviceModel, ReportModel } from '@/store/types';
import { Route } from 'vue-router';
import NetworkName from '@/components/NetworkName.vue';
import DevicesStatus from '@/components/DevicesStatus.vue';
import DeviceAdder from '@/components/DeviceAdder.vue';
import NetworkDiagram from '@/components/NetworkDiagram.vue';
import DeviceSdkInstaller from '@/components/DeviceSdkInstaller.vue';
import { notifyDeviceAdded, notifyDeviceRevival, notifyDeviceDeath } from '@/notifications/notifications';
import { rangedLatestStatus } from '@/helper/deviceStatus';
import deepMapKeys from '@/helper/deepMapKeys';
import camelCase from 'lodash.camelcase';
import { Authentication, checkAuthentication } from '@/components/Authentication';

@Component({
  components: {
    NetworkName,
    DevicesStatus,
    DeviceAdder,
    NetworkDiagram,
    DeviceSdkInstaller,
  },
  beforeRouteEnter(to, from, next) {
    checkAuthentication(next);
  },
  beforeRouteUpdate(to, from, next) {
    checkAuthentication(next);
  },
})
export default class Network extends Vue {
  // private network = {} as NetworkModel;
  private devicesFromAPI = [] as DeviceModel[];
  private reportsFromAPI = [] as ReportModel[];
  private socket = {} as WebSocket;
  private deviceAdderOpened = false;
  private sdkInstallerOpened = false;
  private addedDeviceKind = '';
  private addedDeviceId = '';
  private initialized = false;

  /*@Watch('$route') // ONLY NEED FOR NETWORK PAGE
  private onRouteChanged(newRoute: Route, oldRoute: Route) {
    this.stopFetchingTimer();
    this.prepareNetworkData(newRoute.params.networkId);
  }*/

  private async created() {
    this.prepareNetworkData(/*this.$route.params.networkId*/);
    this.initializeWebSocket();
  }

  private beforeDestroy() {
    this.socket.close();
  }

  private get candidatesAreExisting() {
    return this.deviceCandidates.length > 1;
  }

  private get devices() {
    return this.devicesFromAPI.filter((device) => !device.isCandidate).map((device) => {
      return {
        ...device,
        reports: this.reportsOfDevice(device.deviceId),
      };
    });
  }

  private get deviceCandidates() {
    return this.devicesFromAPI.filter((device) => device.isCandidate).map((device) => {
      // const deviceWithReports: DeviceModel = { ...device };
      // deviceWithReports.reports = this.reportsFromAPI;
      // return deviceWithReports;
      return {
        ...device,
        reports: this.reportsOfDevice(device.deviceId),
      }
    });
  }

  private get reportsOfDevice() {
    const self = this;
    return function(deviceId: string) {
      return self.reportsFromAPI.filter((report) => report.deviceId === deviceId);
    }
  }

  private get deviceTree() {
    return {
      id: '',
      name: 'motch',
      reports: [],
      children: this.children(undefined),
      status: 'alive',
    };
  }

  private get children() {
    const self = this;
    return function(parentId: string | undefined) {
      const children = self.devices.filter((device) => device.parentId === parentId);
      return children.map((device) => {
        // error is caused from device.deviceId === undefined
        return {
          ...device,
          id: device.deviceId,
          children: self.children(device.deviceId),
          status: self.latestStatus(device.deviceId),
        };
      });
    };
  }

  private get latestStatus() {
    const self = this;
    return function(deviceId: string) {
      // should be written in helper/*.ts
      const device = self.devices.find((dev) => dev.deviceId === deviceId);
      if (device === undefined) {
        return '';
      }
      const reports = device.reports;
      if (reports.length < 1) {
        return 'dead';
      }
      return device.reports.sort((a, b) => b.createdAt - a.createdAt)[0].status;
    };
  }

  private async prepareNetworkData(/*networkId: string*/) {
    // this.network = { networkId, name: '', devices: [] };
    this.initialized = false;
    await this.fetch();
    this.initialized = true;
  }

  private onMessageReceived(ev: MessageEvent) {
    const message = deepMapKeys(JSON.parse(ev.data), (val, key) => camelCase(key));
    const eventType = message.eventType as string;

    if (eventType === 'device_created' || eventType === 'device_updated') {
      const updatedDevice = message.device as DeviceModel;
      const currentDeviceIndex = this.devicesFromAPI.findIndex((dev) => dev.deviceId === updatedDevice.deviceId);

      if (currentDeviceIndex < 0) {
        // add device to device list on API
        this.devicesFromAPI.push(updatedDevice);
        notifyDeviceAdded(updatedDevice.name);
        if (eventType === 'device_created') {
          this.openSDKInstaller(updatedDevice.deviceId);
        }
        // console.log('created', updatedDevice.deviceId);
        return;
      }
      // overwrite current device which deviceId is updatedDevice.deviceId
      const currentDevice = this.devicesFromAPI[currentDeviceIndex];
      this.devicesFromAPI.splice(currentDeviceIndex, 1, {
        ...currentDevice,
        ...updatedDevice,
      });
      // console.log('updated', updatedDevice.deviceId);
    } else if (eventType === 'device_deleted') {
      const deletedDevice: {deviceId: string, userId: string} = message.device;
      // delete device from devicesFromAPI
      this.devicesFromAPI = this.devicesFromAPI.filter((dev) => dev.deviceId !== deletedDevice.deviceId);
    } else if (eventType === 'device_status_changed') {
      const report: ReportModel = message.report;
      this.updateReport(report);
      const device = this.devices.find((d) => d.deviceId === report.deviceId);
      if (device === undefined) {
        return;
      }

      if (report.status === 'alive') {
        notifyDeviceRevival(device.name);
      } else if (report.status === 'dead') {
        notifyDeviceDeath(device.name);
      } else {
        // unknown status change
      }
    } else {
      // console.warn('unknown message has arrived');
    }
    return;
  }

  private updateReport(report: ReportModel) {
    const currentReportIndex = this.reportsFromAPI.findIndex((rep) => rep.deviceId === report.deviceId && rep.createdAt === report.createdAt);
    if (currentReportIndex < 0) {
      this.reportsFromAPI.push(report);
      // console.log('created', report.deviceId, report.status);
      return;
    }
    const currentReport = this.reportsFromAPI[currentReportIndex];
    this.reportsFromAPI.splice(currentReportIndex, 1, {
      ...currentReport,
      ...report,
    });
    // console.log('updated', report.deviceId, report.status);
  }

  private async fetch() {
    // await this.fetchNetwork();
    await this.fetchDevices();
    await this.fetchReports();
  }
/*
  private async fetchNetwork() {
    this.network = await NetworksRepository.get(this.network.networkId)
      .then((res) => res.data);
      // .catch((err) => console.log(err));
  }*/

  private async fetchDevices() {
    this.devicesFromAPI = await DevicesRepository.getAll()
      .then((res) => res.data)
      .then((devices) => devices.map((device) => {
        const result = { ...device };
        if (result.isCandidate === undefined) {
          result.isCandidate = false;
        }
        return result;
      }));
  }

  private async fetchReports() {
    const reportsOfDevices = await Promise.all(
      this.devicesFromAPI.map((device) => ReportsRepository.getByDeviceId(device.deviceId).then((res) => res.data)));
    this.reportsFromAPI = reportsOfDevices.length > 0 ? reportsOfDevices.reduce((reports, r) => reports.concat(r)) : [] as ReportModel[];
  }

  private initializeWebSocket() {
    Authentication.idToken.then((idToken) => {
      if (process.env.VUE_APP_WS_URL === undefined) {
        alert('リアルタイム更新を開始できませんでした。再読み込みしてください。');
      } else {
        this.socket = new WebSocket(`${process.env.VUE_APP_WS_URL}?idToken=${idToken}`);
      }
      // this.socket.onopen = (ev) => console.log('Real time notification started.');
      this.socket.onmessage = (ev) => this.onMessageReceived(ev);
      // this.socket.onclose = (ev) => console.log('socket closed');
    });
  }

  private getDeviceName(deviceId: string) {
    const device = this.devices.find((dev) => dev.deviceId === deviceId);
    if (device === undefined) { return ''; }
    return device.name;
  }

  private async setDeviceAsApproved(deviceId: string) {
    const device = await DevicesRepository.get(deviceId).then((res) => res.data);
    device.isCandidate = false;
    DevicesRepository.put(device, ['isCandidate']);
  }

  private async deleteDevice(deviceId: string) {
    await DevicesRepository.delete(deviceId);
    this.fetchDevices();
  }

  private openDeviceAdder() {
    this.deviceAdderOpened = true;
  }

  private closeDeviceAdder(request: string, event) {
    this.deviceAdderOpened = false;
  }

  private openSDKInstaller(deviceId: string) {
    this.addedDeviceId = deviceId;
    const device = this.devices.find((dev) => dev.deviceId === deviceId);
    if (device === undefined) { return; }
    this.addedDeviceKind = device.kind;
    this.sdkInstallerOpened = true;
  }

  private closeSDKInstaller() {
    this.addedDeviceId = '';
    this.addedDeviceKind = '';
    this.sdkInstallerOpened = false;
  }
}
